import React, { useEffect, useState } from "react";
import { ShoppingBag, DollarSign, Receipt, Wallet, Clock, Boxes } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, num, Spinner, PageHeader, StatusPill, Empty } from "@/components/shared";

export default function ClientPortal() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [orders, setOrders] = useState([]);
  const [recs, setRecs] = useState([]);

  useEffect(() => {
    api.get("/dashboard/client").then((r) => setD(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data.slice(0, 8))).catch(() => setOrders([]));
    api.get("/recommendations").then((r) => setRecs(r.data)).catch(() => {});
  }, []);

  if (!d) return <Spinner label="Loading your portal" />;

  const cards = [
    { title: "Orders Today", value: num(d.orders_today), icon: ShoppingBag, tint: "text-sky-400" },
    { title: "Sales Today", value: money(d.sales_today), icon: DollarSign, tint: "text-emerald-400" },
    { title: "Consumption Cost", value: money(d.consumption_today), icon: Receipt, tint: "text-amber-400" },
    { title: "Current Balance", value: money(d.balance?.current_balance), icon: Wallet, tint: "text-rose-300" },
  ];

  return (
    <div>
      <PageHeader title={d.client_name} subtitle="Your brand dashboard — today at Dish Factory" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {cards.map((c) => (
          <div key={c.title} data-testid={`portal-kpi-${c.title.toLowerCase().replace(/\s/g, "-")}`} className="card-surface p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-mono">{c.title}</span>
              <c.icon className={`h-4 w-4 ${c.tint}`} />
            </div>
            <p className="text-2xl sm:text-3xl font-extrabold font-mono text-white">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="card-surface p-5">
          <div className="flex items-center gap-2 mb-4"><Clock className="h-4 w-4 text-amber-400" /><h3 className="font-display font-semibold text-white">Recent Orders</h3></div>
          {orders.length === 0 ? <Empty icon={Clock} title="No orders yet" /> : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`portal-order-${o.id}`}>
                  <div>
                    <p className="text-sm font-medium text-white">{o.order_ref}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      Recv {o.received_at ? new Date(o.received_at).toLocaleTimeString() : "—"}
                      {o.ready_at && ` · Ready ${new Date(o.ready_at).toLocaleTimeString()}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <StatusPill status={o.status} />
                    <p className="text-xs font-mono text-slate-300 mt-1">{money(o.total_sales)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-surface p-5">
          <div className="flex items-center gap-2 mb-4"><Boxes className="h-4 w-4 text-amber-400" /><h3 className="font-display font-semibold text-white">Inventory Recommendations</h3></div>
          {recs.length === 0 ? <Empty icon={Boxes} title="No recommendations recorded" /> : (
            <div className="space-y-2">
              {recs.map((r) => (
                <div key={r.id} className="rounded-lg border border-border p-3" data-testid={`portal-rec-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-white">{r.ingredient_name}</p>
                    <p className="text-xs font-mono text-amber-300">Approved {num(r.client_approved_qty)}</p>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono mt-1">
                    Min {num(r.minimum_level)} · Reorder {num(r.reorder_threshold)} · Available {num(r.current_quantity)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {!d.cost_visibility && (
        <p className="text-xs text-muted-foreground mt-6">Note: recipe cost visibility is disabled for your account by Dish Factory.</p>
      )}
    </div>
  );
}
